import React, { useEffect, useState, useRef } from "react";

const DEFAULT_THRESHOLDS = { pingGood: 100, pingWarn: 250, speedGood: 3.5, speedWarn: 1 };

function msToMbps(bytes, ms) {
  if (!ms || ms <= 0) return 0;
  const bits = bytes * 8;
  const seconds = ms / 1000;
  return (bits / seconds) / (1024 * 1024);
}

export default function InternetCheck({
  runFullTestIfUnknown = true,
  thresholds = DEFAULT_THRESHOLDS,
  onResult = () => {},
  showUI = true,
}) {
  const [status, setStatus] = useState("checking"); // checking | good | warn | bad
  const [message, setMessage] = useState("Verificando conexão...");
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    (async () => {
      try {
        const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (/*conn && conn.effectiveType*/false) {
          const { effectiveType, downlink } = conn;
          if (effectiveType === "4g" && downlink >= thresholds.speedGood) {
            updateState("good", "Conexão rápida detectada.");
            return;
          }
        }

        if (!runFullTestIfUnknown) {
          updateState("warn", "Não foi possível confirmar velocidade. Continuando...");
          return;
        }

        const pingMs = await measurePing("/api/ping", 3);
        const downloadResult = await measureDownload("/api/speed-test/256kb");
        const mbps = msToMbps(downloadResult.bytes, downloadResult.timeMs);

        let newState = "good";
        let msg = `Ping ${Math.round(pingMs)} ms — Download ~${mbps.toFixed(2)} Mbps`;

        if (pingMs > thresholds.pingWarn || mbps < thresholds.speedWarn) {
          newState = "bad"; msg = `Conexão lenta/instável — ${msg}`;
        } else if (pingMs > thresholds.pingGood || mbps < thresholds.speedGood) {
          newState = "warn"; msg = `Conexão moderada — ${msg}`;
        } else {
          newState = "good"; msg = `Conexão OK — ${msg}`;
        }

        updateState(newState, msg);
        onResult({ pingMs, mbps, state: newState });
        // cache leve para a sessão
        try { sessionStorage.setItem("internet-check", JSON.stringify({ state: newState, msg })); } catch {}
      } catch (err) {
        console.error("InternetCheck error", err);
        updateState("warn", "Não foi possível testar a conexão — prosseguindo.");
        onResult({ error: err });
      }
    })();
    return () => { isMounted.current = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateState(newState, newMessage) {
    if (!isMounted.current) return;
    setStatus(newState); setMessage(newMessage);
  }

  async function measurePing(url = "/api/ping", tries = 3) {
    const results = [];
    for (let i = 0; i < tries; i++) {
      const start = performance.now();
      await fetch(`${url}?_=${Date.now()}${Math.random()}`, { method: "GET", cache: "no-store" });
      results.push(performance.now() - start);
      await new Promise(r => setTimeout(r, 120));
    }
    results.sort((a,b)=>a-b);
    return results[Math.floor(results.length/2)];
  }

  async function measureDownload(url = "/api/speed-test/256kb") {
    const start = performance.now();
    const res = await fetch(`${url}?_=${Date.now()}${Math.random()}`, { cache: "no-store" });
    const arrayBuffer = await res.arrayBuffer();
    return { timeMs: performance.now() - start, bytes: arrayBuffer.byteLength };
  }

  /*  function withTimeout(ms, signalFromParent, task) {
  const ctrl = new AbortController();
  const timeout = setTimeout(() => ctrl.abort("timeout"), ms);
  const signal = signalFromParent ?? ctrl.signal;

  return task(signal)
    .finally(() => clearTimeout(timeout));
}

// Ping com timeout e HEAD (mais leve). Cai para GET se HEAD falhar.
async function measurePing(url = "/api/ping", tries = 3, timeoutMs = 5000) {
  const results = [];
  for (let i = 0; i < tries; i++) {
    const start = performance.now();
    try {
      await withTimeout(timeoutMs, null, async (signal) => {
        const res = await fetch(`${url}?_=${Date.now()}${Math.random()}`, {
          method: "HEAD",
          cache: "no-store",
          signal
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
      });
      results.push(performance.now() - start);
    } catch (e) {
      // fallback GET simples
      const start2 = performance.now();
      await withTimeout(timeoutMs, null, async (signal) => {
        const res = await fetch(`${url}?_=${Date.now()}${Math.random()}`, {
          method: "GET",
          cache: "no-store",
          signal
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
      });
      results.push(performance.now() - start2);
    }
    await new Promise(r => setTimeout(r, 120));
  }
  results.sort((a,b)=>a-b);
  return results[Math.floor(results.length/2)];
}

// Download com timeout + verificação de bytes
async function measureDownload(url = "/api/speed-test/1mb", timeoutMs = 8000) {
  const start = performance.now();
  const res = await withTimeout(timeoutMs, null, (signal) =>
    fetch(`${url}?_=${Date.now()}${Math.random()}`, { cache: "no-store", signal })
  );
  if (!res.ok) throw new Error(`download status ${res.status}`);
  const arrayBuffer = await res.arrayBuffer();
  const timeMs = performance.now() - start;
  const bytes = arrayBuffer.byteLength;
  if (bytes === 0) throw new Error("download vazio");
  return { timeMs, bytes };
}*/

  if (!showUI) return null;

  if (status === "checking" || status === "good") return null;


  return (
    <div style={{
      padding: 12, borderRadius: 8,
      background: status === "good" ? "#e6ffed" : status === "warn" ? "#fff6e6" : "#ffe6e6",
      color: "#111", marginBottom: 12, fontSize: 14
    }}>
      <strong>{status === "good" ? "Conexão: OK" : status === "warn" ? "Conexão: Moderada" : "Conexão: Lenta/Instável"}</strong>
      <div style={{ marginTop: 6 }}>{message}</div>
      {status !== "good" && (
        <div style={{ marginTop: 8 }}>
          <small>Sugestões: tente um Wi-Fi mais estável, feche apps que usam muita banda ou continue mesmo assim.</small>
        </div>
      )}
    </div>
  );
}
